/**
 * La BANQUE DE SAVOIRS — taxonomie partagée et contrats de la console d'édition.
 *
 * Ce fichier ne porte que des types et des constantes : la validation, elle,
 * vit côté API (`modules/corpus/validation.ts`) et s'exécute AUX DEUX BOUTS —
 * à la génération comme à l'édition humaine. Un pédagogue qui corrige un item
 * dans la console peut casser ce que le générateur respectait ; c'est pour ça
 * que la règle ne vit pas dans le générateur.
 *
 * ─── La hiérarchie, en sept niveaux ────────────────────────────────────────
 *
 *   Matière -> Niveau -> Compétence -> Savoir -> Notion -> ErreurType -> Item
 *
 * Les deux premiers sont des enums (`Matiere`, `NiveauScolaire`). La compétence
 * et le savoir existaient déjà. `Notion`, `ErreurType` et les champs de corpus
 * de l'item sont posés par le Sprint 24.
 */
/* -------------------------------------------------------------------------- */
/* 1. La taxonomie                                                            */
/* -------------------------------------------------------------------------- */
/**
 * Le degré d'exigence VISÉ par l'auteur — une échelle établie a priori.
 *
 * Un pédagogue lit les objectifs du programme pour une notion et gradue ses
 * énoncés, exactement comme un manuel arrive avec des exercices gradués. C'est
 * la valeur de DÉPART, pas un pis-aller en attendant les statistiques d'usage.
 *
 * Ce qu'elle n'est pas : un taux de réussite observé. Les deux vivent dans des
 * champs séparés et aucune surface ne montre jamais l'un pour l'autre.
 */
export const EXIGENCES_COGNITIVES = ['accessible', 'intermediaire', 'exigeant'];
/** QUI tranche le juste/faux d'un item, et par quel moyen. */
export const METHODES_VERIFICATION = [
    'deterministe',
    'cas',
    'grille_modele',
    'grille_humaine',
];
/** La famille d'une conception fausse — sert à regrouper les remédiations. */
export const CATEGORIES_ERREUR = [
    'procedurale',
    'conceptuelle',
    'lecture_enonce',
    'lecture_figure',
    'langue',
    'methode',
];
/** Qui a rendu un verdict mémorisé. */
export const SOURCES_VERDICT = ['deterministe', 'cas', 'modele', 'pedagogue'];
/** L'état d'un verdict mémorisé. */
export const STATUTS_VERDICT_CACHE = [
    'automatique',
    'confirme_pedagogue',
    'corrige_pedagogue',
];
/**
 * Où en est la RELECTURE d'un item — et ce n'est PAS une porte.
 *
 * Décision du fondateur (2026-09-03) : le corpus est publié d'abord, relu
 * ensuite. Cet état progresse pendant que l'item est en service ; c'est
 * `ContentStatus` qui décide du service, jamais celui-ci.
 *
 * `a_corriger` (refonte de la console, 15 octobre 2026, décision D-1) : lu, et
 * quelque chose est à reprendre DANS LA SOURCE, avec une note. Seuls les
 * exercices du manuel le portent aujourd'hui.
 */
export const STATUTS_RELECTURE = ['genere', 'relu', 'corrige', 'a_corriger'];
/**
 * La PORTÉE d'une correction sur un item déjà servi — le champ qui décide si
 * l'histoire des mesures reste vraie.
 *
 * `forme` : l'habillage change, ce qui est demandé et la réponse attendue ne
 * changent pas. L'item garde son identité, sa version avance, l'ancien texte
 * est archivé.
 *
 * `fond` : les données, la question, la réponse attendue ou le savoir visé
 * changent. Ce n'est plus le même item : l'ancien est retiré du service (il
 * reste lisible), un item neuf le remplace.
 */
export const PORTEES_REVISION = ['forme', 'fond'];
/* -------------------------------------------------------------------------- */
/* 2. Les plafonds du mandat                                                  */
/* -------------------------------------------------------------------------- */
/**
 * Les plafonds, écrits une fois et vérifiés partout.
 *
 * « 8 items par savoir, 3 accessibles / 3 intermédiaires / 2 exigeants,
 * plafond dur. Au moins 2 erreurs types par savoir. Si un savoir ne mérite pas
 * 8 items, en écrire moins ET DIRE POURQUOI plutôt que de remplir. »
 *
 * Le dernier point est ce qui rend ces constantes utilisables : le validateur
 * accepte un savoir sous-doté à condition qu'il porte une justification écrite
 * (`justificationSousDotation`). Sans elle, il refuse.
 */
export const PLAFONDS_CORPUS = {
    /**
     * Plafond DUR : jamais plus de 12 items pour un savoir — Sprint 32.
     *
     * Il valait 8. Le fondateur l'a porté à 12 le 7 septembre, et la raison n'est
     * pas « plus, c'est mieux » : à huit items, un élève qui travaille le même
     * savoir plusieurs jours de suite revoit les mêmes exercices, et la règle de
     * reprise différée (sept jours) n'a plus de quoi choisir. Douze laissent au
     * sélecteur adaptatif de la marge sans faire de la banque un catalogue.
     *
     * Le plafond reste DUR : treize items, c'est un refus. Un savoir qui en
     * demande plus est un savoir qui devrait être deux.
     */
    itemsParSavoir: 12,
    /**
     * La CIBLE d'un savoir neuf, et la répartition qui va avec.
     *
     * 4 / 5 / 3 plutôt que 3 / 3 / 2 mis à l'échelle : c'est l'INTERMÉDIAIRE qui
     * gagne le plus, parce que c'est là que se passe l'essentiel du travail
     * quotidien. L'accessible sert à entrer dans la notion, l'exigeant à la
     * fermer ; ni l'un ni l'autre n'a besoin d'autant de variété.
     *
     * La répartition n'est VÉRIFIÉE qu'à la cible. En dessous, le savoir est
     * en cours d'écriture et sa forme n'est pas encore jugeable.
     */
    cibleParSavoir: 12,
    repartition: {
        accessible: 4,
        intermediaire: 5,
        exigeant: 3,
    },
    /**
     * Le plancher sous lequel un savoir doit DIRE POURQUOI il est incomplet.
     *
     * Il vaut 8, et c'est délibérément l'ancienne cible : les 120 savoirs écrits
     * avant le Sprint 32 en portent exactement huit, et ils restent servables
     * sans qu'on leur demande une justification rétroactive pour une règle qui
     * n'existait pas quand on les a écrits.
     *
     * Relever la cible n'invalide pas ce qui a été fait sous l'ancienne. C'est
     * la différence entre une exigence qui monte et une dette qu'on fabrique.
     */
    minimumSansJustification: 8,
    /** La répartition attendue d'un savoir écrit sous l'ancienne cible. */
    repartitionHeritee: {
        accessible: 3,
        intermediaire: 3,
        exigeant: 2,
    },
    /**
     * Le SEUIL DE SERVICE — Sprint 32.
     *
     * En dessous de trois items publiés, un savoir cesse d'être VISÉ par le
     * sélecteur adaptatif. Il reste mesurable, il reste atteignable si l'élève
     * le choisit lui-même ; simplement, le plan ne l'ordonne plus.
     *
     * C'est le garde-fou du retrait : un savoir que le plan vise et que la tâche
     * du jour ne peut pas remplir produit exactement la rupture entre deux
     * parties du système qu'on cherche à éviter — un écran qui promet et un
     * autre qui n'a rien.
     *
     * Trois, et pas un : avec un seul item, la reprise différée ne peut rien
     * proposer dès le lendemain.
     */
    seuilDeService: 3,
    /** Plancher : au moins 2 conceptions fausses documentées par savoir. */
    erreursTypesParSavoirMin: 2,
    /** Bornes de l'échelle fine d'auteur. */
    difficulteAuteurMin: 1,
    difficulteAuteurMax: 5,
};
/**
 * L'échelle d'auteur, cran par cran, pour que trois personnes qui écrivent le
 * corpus posent le même chiffre devant la même tâche. Sans cette table, la
 * « difficulté d'auteur » est une opinion ; avec elle, c'est un critère.
 */
export const BORNES_DIFFICULTE_PAR_EXIGENCE = {
    /** Une seule étape, données directement utilisables, vocabulaire du cours. */
    accessible: { min: 1, max: 2 },
    /** Deux étapes, ou une étape avec une donnée à extraire ou à convertir. */
    intermediaire: { min: 2, max: 4 },
    /** Trois étapes ou plus, ou un retour en arrière (« quelle largeur ? »). */
    exigeant: { min: 4, max: 5 },
};
//# sourceMappingURL=corpus.js.map